import '../../platform.css';
import './ownTeam.css';

import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../recoil/atoms/userSettingsState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { initialOwnTeamTableFiltersState, ownTeamTableFiltersState } from '../../recoil/atoms/ownTeamTableFiltersState';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';

import { translate } from '../../../common/language/translations';
import { DropDown } from '../../components/controls/dropDowns/DropDown';
import { updateSeasonStatsToggles } from '../../services/firestore/userSettings';
import { DropDownWithIcons } from '../../components/controls/dropDowns/DropDownWithIcons';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { OwnTeamTable } from '../../components/tables/ownTeamTable/OwnTeamTable';
import { PlayerId, PlayerMap } from '../../types/player';
import { StringToAnyMap } from '../../types/common';
import { playerRoleToSortOrder, teamRoles } from '../../static/constants/playerRoles';
import { isValidPlayerLocality, isValidPlayerRole } from '../../utils/playerUtils';
import { playerLocalityToSortOrder } from '../../static/constants/playerLocalities';
import { getAge } from '../../utils/ageUtils';
import { getMonthsLeftOfContract } from '../../utils/contractUtils';
import { getNextYears } from '../../utils/dateUtils';
import { ageKeys } from '../../static/constants/scoutingFilters';


type OwnTeamFilters = 'maxAge' | 'contractStatus' | 'nationalities' | 'localities' | 'teamRoles';

interface OwnTeamTableViewProps {
  ownTeamPlayers: PlayerMap;
}

export const OwnTeamTableView: React.FC<OwnTeamTableViewProps> = ({
  ownTeamPlayers
}) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);

  const [ownTeamTableFilters, setOwnTeamTableFilters] = useRecoilState(ownTeamTableFiltersState);

  const [tableData, setTableData] = useState<StringToAnyMap[]>([]);

  // updates to the table will trigger a new prepareTableData call, and we want to maintain the current order in view when providing table data
  const [lastTableSortBy, setLastTableSortBy] = useState<StringToAnyMap | undefined>(undefined);
  const [lastTableSortedOrder, setLastTableSortedOrder] = useState<PlayerId[] | undefined>(undefined);

  const [showFilters, setShowFilters] = useState(userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded);

  const [countryDropDownOptions, setCountryDropDownOptions] = useState<string[]>([]);

  const [currentFilterExpanded, setCurrentFilterExpanded] = useState<OwnTeamFilters | undefined>(undefined);


  const handleToggleShowFilters = async () => {
    if (!userConfig) return;

    const actionIsToggleOff = userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded;
    if (actionIsToggleOff) {
      setOwnTeamTableFilters(initialOwnTeamTableFiltersState);
    }

    const newSeasonStatsToggles = { isOwnTeamTableFilterSectionExpanded: !userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded };
    await updateSeasonStatsToggles(newSeasonStatsToggles, userConfig.email, userConfig.club, currentUser);
  };


  const prepareTableData = () => {

    // sort data
    let newTableData: StringToAnyMap[] = [];
    if (!lastTableSortedOrder || ownTeamTableFilters.tableSortBy !== lastTableSortBy) {

      newTableData = Object.values(ownTeamPlayers);
      newTableData.sort((a: StringToAnyMap, b: StringToAnyMap) => {
        const tableSortByToUse = ownTeamTableFilters.tableSortBy ?? { id: 'role', desc: true };

        const sortProperty = tableSortByToUse.id;
        let oneValue = tableSortByToUse.desc ? 1 : -1;
        let minusOneValue = tableSortByToUse.desc ? -1 : 1;

        if (tableSortByToUse.id === 'birth_date') {
          oneValue = oneValue * -1;
          minusOneValue = minusOneValue * -1;
        }

        if (!a[sortProperty]) return oneValue;
        if (!b[sortProperty]) return minusOneValue;

        const aValue = tableSortByToUse.id === 'role'
          ? (isValidPlayerRole(a.role) ? playerRoleToSortOrder[a.role] : 0)
          : tableSortByToUse.id === 'locality'
            ? (isValidPlayerLocality(a.locality) ? playerLocalityToSortOrder[a.locality] : 0)
            : a[sortProperty];

        const bValue = tableSortByToUse.id === 'role'
          ? (isValidPlayerRole(b.role) ? playerRoleToSortOrder[b.role] : 0)
          : tableSortByToUse.id === 'locality'
            ? (isValidPlayerLocality(b.locality) ? playerLocalityToSortOrder[b.locality] : 0)
            : b[sortProperty];

        return aValue > bValue ? minusOneValue : oneValue;
      });
    }
    else {
      newTableData = lastTableSortedOrder.filter(id => ownTeamPlayers[id]).map(id => ownTeamPlayers[id]);

      // not needed
      const newPlayersAdded = Object.values(ownTeamPlayers).filter((player: StringToAnyMap) => !lastTableSortedOrder.includes(player.id));
      newTableData = newTableData.concat(newPlayersAdded);
    }

    setLastTableSortedOrder(newTableData.map((player: StringToAnyMap) => player.id));
    setLastTableSortBy(ownTeamTableFilters.tableSortBy);

    if (ownTeamTableFilters.selectedMaxAge.length > 0) {
      const maxAge = Number(ownTeamTableFilters.selectedMaxAge[0]);
      newTableData = newTableData.filter(
        player => {
          const age = getAge(player.birth_date);
          return age && age <= maxAge;
        }
      );
    }

    if (ownTeamTableFilters.selectedContractStatus.length > 0) {
      newTableData = newTableData.filter(
        player => {
          if (!player.contract_expiration) return false;

          const contractExpirationYear = player.contract_expiration.split('-')[0];
          return ownTeamTableFilters.selectedContractStatus.includes(contractExpirationYear);
        }
      );
    }

    if (ownTeamTableFilters.selectedNationalities.length > 0) {
      newTableData = newTableData.filter(
        player => ownTeamTableFilters.selectedNationalities.includes(player.country_code)
      );
    }

    if (ownTeamTableFilters.selectedLocalities.length > 0) {
      newTableData = newTableData.filter(
        player => ownTeamTableFilters.selectedLocalities.includes(player.locality)
      );
    }

    if (ownTeamTableFilters.selectedTeamRoles.length > 0) {
      newTableData = newTableData.filter(
        player => ownTeamTableFilters.selectedTeamRoles.includes(player.role)
      );
    }


    // compute summary data
    let totalNumberOfPlayers = 0;
    let [totalAge, playersWithAge]: [number, number] = [0, 0];
    let [totalMonthsLeftOfContract, playersWithMonthsLeftOfContract] = [0, 0];

    let totalMarketValue = 0;
    let totalSigningCost = 0;
    let totalSalary = 0;
    let totalBonuses = 0;

    const countryCodeMap: { [key: string]: number } = {};
    const localityMap: { [key: string]: number } = {};
    const roleMap: { [key: string]: number } = {};

    newTableData.forEach((player: StringToAnyMap) => {
      totalNumberOfPlayers++;

      if (player.age) {
        totalAge += player.age;
        playersWithAge++;
      }

      if (player.contract_expiration) {
        totalMonthsLeftOfContract += getMonthsLeftOfContract(player.contract_expiration) as number;
        playersWithMonthsLeftOfContract++;
      }

      if (player.market_value) totalMarketValue += player.market_value;
      if (player.signingCost) totalSigningCost += player.signingCost;
      if (player.salary) totalSalary += player.salary;
      if (player.bonuses) totalBonuses += player.bonuses;

      if (player.country_code) {
        if (countryCodeMap[player.country_code]) {
          countryCodeMap[player.country_code]++;
        }
        else {
          countryCodeMap[player.country_code] = 1;
        }
      }

      if (player.locality) {
        if (localityMap[player.locality]) {
          localityMap[player.locality]++;
        }
        else {
          localityMap[player.locality] = 1;
        }
      }

      if (player.role) {
        if (roleMap[player.role]) {
          roleMap[player.role]++;
        }
        else {
          roleMap[player.role] = 1;
        }
      }
    });

    const nationalCountryCode = tenantSettings?.countryCode ?? 'NO';
    let numberOfNatives = 0;
    let numberOfForeigners = 0;
    for (const code in countryCodeMap) {
      if (code === nationalCountryCode) {
        numberOfNatives += countryCodeMap[code];
      }
      else {
        numberOfForeigners += countryCodeMap[code];
      }
    }

    newTableData.push({
      'name': 'tableSummary',

      'averageAge': playersWithAge ? (Math.round((totalAge / playersWithAge) * 10) / 10) : undefined,
      'averageMonthsLeftOfContract': playersWithMonthsLeftOfContract ? Math.round(totalMonthsLeftOfContract / playersWithMonthsLeftOfContract) : undefined,

      'market_valueTotal': totalMarketValue ? Math.round(totalMarketValue) : undefined,
      'signingCostTotal': totalSigningCost ? Math.round(totalSigningCost) : undefined,
      'salaryTotal': totalSalary ? Math.round(totalSalary) : undefined,
      'bonusesTotal': totalBonuses ? Math.round(totalBonuses) : undefined,

      'numberOfLocalPlayers': localityMap['local'] ?? 0,
      'numberOfNationalPlayers': localityMap['national'] ?? 0,
      'numberOfInternationalPlayers': localityMap['international'] ?? 0,

      'nationalCountryCode': nationalCountryCode,
      'numberOfNatives': numberOfNatives,
      'numberOfForeigners': numberOfForeigners,

      'roleMap': roleMap,

      'numberOfPlayers': totalNumberOfPlayers,
    });

    setTableData(newTableData);
  };


  useEffect(() => {
    prepareTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownTeamPlayers,
    ownTeamTableFilters,
  ]);


  useEffect(() => {
    const countryCodeMap: { [key: string]: number } = {};
    Object.values(ownTeamPlayers).forEach((player: StringToAnyMap) => {
      if (player.country_code) {
        if (countryCodeMap[player.country_code]) {
          countryCodeMap[player.country_code]++;
        }
        else {
          countryCodeMap[player.country_code] = 1;
        }
      }
    });
    const countryCodes = Object.keys(countryCodeMap).sort((a: string, b: string) => countryCodeMap[b] - countryCodeMap[a]);
    setCountryDropDownOptions(countryCodes);

    // if selectedNationalities contains a country code that is not in the countryCodes array, remove it
    const newSelectedNationalities = ownTeamTableFilters.selectedNationalities.filter((nationality: string) => countryCodes.includes(nationality));
    setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedNationalities: newSelectedNationalities });
  }, [ownTeamPlayers]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (!userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded) {
      setShowFilters(false);
    }
    else {
      const timer = setTimeout(() => {
        setShowFilters(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded]);


  return (
    <div className='full-size-container fast-fade-in'>

      {currentFilterExpanded && <div className='filter-section-empty-background' onClick={() => setCurrentFilterExpanded(undefined)} />}

      <IconButton
        onClick={handleToggleShowFilters}
        icon={
          userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded
            ? <CloseIcon style={{ fontSize: 20 }} />
            : <FilterAltIcon style={{ fontSize: 20, marginTop: 1 }} />
        }
        size={24}
        style={{ position: 'absolute', top: 19, left: 22, zIndex: 6 }}
        startWhite={true}
        title={!userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded ? translate('showFilters', userConfig?.language) : undefined}
      />

      {userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded && showFilters && (
        <div className='own-team-table-filter-section'>
          <div className='filter-section-drop-down-title own-team-table-filter-section-age'>
            {translate('maxAge', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down own-team-table-filter-section-age'>
            <DropDown
              id={'own-team-table-filter-section-age'}
              dropDownOptions={ageKeys}
              selectedOptions={ownTeamTableFilters.selectedMaxAge}
              setSelectedOptions={(value: number[]) => setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedMaxAge: value })}
              isDropDownExpanded={currentFilterExpanded === 'maxAge'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'maxAge' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
            />
          </div>

          <div className='filter-section-drop-down-title own-team-table-filter-section-contract'>
            {translate('contractExpiration', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down own-team-table-filter-section-contract'>
            <DropDown
              id={'own-team-table-filter-section-contract'}
              dropDownOptions={getNextYears(8)}
              selectedOptions={ownTeamTableFilters.selectedContractStatus}
              setSelectedOptions={(value: number[]) => setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedContractStatus: value })}
              isDropDownExpanded={currentFilterExpanded === 'contractStatus'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'contractStatus' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              multiSelect={true}
            />
          </div>

          <div className='filter-section-drop-down-title own-team-table-filter-section-country'>
            {translate('country', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down own-team-table-filter-section-country'>
            <DropDownWithIcons
              id={'own-team-table-filter-section-country'}
              dropDownOptions={countryDropDownOptions}
              selectedOptions={ownTeamTableFilters.selectedNationalities}
              setSelectedOptions={(value: string[]) => setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedNationalities: value })}
              isDropDownExpanded={currentFilterExpanded === 'nationalities'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'nationalities' : undefined)}
              multiSelect={true}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              useCountryFlags={true}
            />
          </div>

          <div className='filter-section-drop-down-title own-team-table-filter-section-locality'>
            {translate('locality', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down own-team-table-filter-section-locality'>
            <DropDownWithIcons
              id={'own-team-table-filter-section-locality'}
              dropDownOptions={Object.keys(playerLocalityToSortOrder)}
              selectedOptions={ownTeamTableFilters.selectedLocalities}
              setSelectedOptions={(value: string[]) => setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedLocalities: value })}
              isDropDownExpanded={currentFilterExpanded === 'localities'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'localities' : undefined)}
              multiSelect={true}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              useLocalityIcons={true}
            />
          </div>

          {}
          <div className='filter-section-drop-down-title own-team-table-filter-section-role'>
            {translate('teamRole', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down own-team-table-filter-section-role'>
            <DropDownWithIcons
              id={'own-team-table-filter-section-role'}
              dropDownOptions={teamRoles}
              selectedOptions={ownTeamTableFilters.selectedTeamRoles}
              setSelectedOptions={(value: string[]) => setOwnTeamTableFilters({ ...ownTeamTableFilters, selectedTeamRoles: value })}
              isDropDownExpanded={currentFilterExpanded === 'teamRoles'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'teamRoles' : undefined)}
              multiSelect={true}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              useRoleIcons={true}
              dropDownDisabled={!userConfig?.accessConfig.hasAccessToViewTeamRoles}
            />
          </div>

          <IconButton
            onClick={() => setOwnTeamTableFilters(initialOwnTeamTableFiltersState)}
            icon={<ReplayIcon style={{ fontSize: 20 }} />}
            size={24}
            style={{ position: 'absolute', top: 38, right: -44, zIndex: 6 }}
            startWhite={true}
            title={translate('resetFilters', userConfig?.language)}
          />
        </div>
      )}

      <div className='own-team-table-container' style={{ top: userSettings?.seasonStatsToggles?.isOwnTeamTableFilterSectionExpanded ? 86 : 16 }}>
        <OwnTeamTable
          data={tableData}
          tableSortBy={ownTeamTableFilters.tableSortBy}
          setTableSortBy={(value: StringToAnyMap | undefined) => setOwnTeamTableFilters({ ...ownTeamTableFilters, tableSortBy: value })}
        />
      </div>

    </div>
  );
};
